/**
 * «Пользователи и доступ» — единственный экран самой платформы (§13.3).
 *
 * Макет из ТЗ:
 *
 *     Пользователи                                    [+ Пригласить]
 *
 *     Имя              Должность    study    ops    finance
 *     ─────────────────────────────────────────────────────────
 *     Пётр С.  (вы)    —            host   host       host
 *     Анна К.          Менеджер     admin  admin      user
 *     Иван М.          Повар        user   user       —
 *     Мария Л.         Официант     user   —          —
 *
 *     Заявки (2)
 *       Ольга Н. → обучающий сервис, 26.08 14:02   [Открыть]
 *
 * Экран отвечает на вопрос «кто видит финансы?» — ради него и затевался
 * единый вход (§8.1). Поэтому здесь показано ВСЁ: и те, у кого доступа
 * нет (прочерк — такой же ответ, как и уровень).
 *
 * Ни одна кнопка здесь не является защитой. Скрытая кнопка — удобство;
 * право проверяется на сервере на каждом запросе (§6.3), и `admin`,
 * пытающийся выдать `admin` в обход интерфейса, получит отказ от
 * `identity`, а не от этой страницы.
 *
 * На телефоне таблица превращается в карточки: матрица «сотрудник ×
 * сервис» в 360 пикселей шириной не помещается никогда, а горизонтальная
 * прокрутка на строке из четырёх колонок — способ потерять последнюю.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, PendingRequest, PositionOption, UserRow, UsersScreen } from '../shell';
import {
  Button,
  Card,
  Empty,
  ErrorState,
  Field,
  LevelBadge,
  Loading,
  Modal,
  Select,
  api,
  formatShortDateTime,
  serviceTitle,
} from '../shell';
import { RightsTab, UserRightsDialog } from './Rights';

const LEVELS = ['user', 'admin'] as const;

export function UsersAndAccess({ meUserId }: { meUserId: number }) {
  const queryClient = useQueryClient();
  const [invite, setInvite] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ user: UserRow; service: string } | null>(null);
  const [deciding, setDeciding] = useState<PendingRequest | null>(null);
  // Вкладки: «Люди» — карточка: должность и вход в агенты; «Права» —
  // что человеку можно внутри сервиса, по сотрудникам (§8.7, D181).
  const [tab, setTab] = useState<'people' | 'positions'>('people');
  const [rightsOf, setRightsOf] = useState<UserRow | null>(null);

  const screen = useQuery<UsersScreen, ApiError>({
    queryKey: ['platform', 'users'],
    queryFn: api.usersScreen,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });

  const setAccess = useMutation({
    mutationFn: ({ userId, service, level }: { userId: number; service: string; level: string }) =>
      api.setAccess(userId, service, level),
    onSuccess: refresh,
  });

  const revokeAccess = useMutation({
    mutationFn: ({ userId, service }: { userId: number; service: string }) =>
      api.revokeAccess(userId, service),
    onSuccess: refresh,
  });

  const approve = useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: number;
      body: Record<string, unknown>;
    }) => api.approveRequest(requestId, body),
    onSuccess: refresh,
  });

  const reject = useMutation({
    mutationFn: (requestId: number) => api.rejectRequest(requestId, null),
    onSuccess: refresh,
  });

  // Должность меняется прямо в строке, без отдельного окна: это одно
  // значение из готового списка, а не форма. Заведено 08.09.2026 — до
  // этого дня должность проставлялась только при одобрении заявки, то
  // есть поменять её было нельзя вообще никак.
  const setPosition = useMutation({
    mutationFn: ({ userId, positionId }: { userId: number; positionId: number | null }) =>
      api.setPosition(userId, positionId),
    onSuccess: refresh,
  });

  const tabs = (
    <div className="row" style={{ marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
      <Button variant={tab === 'people' ? 'primary' : 'default'} onClick={() => setTab('people')}>
        Люди
      </Button>
      <Button
        variant={tab === 'positions' ? 'primary' : 'default'}
        onClick={() => setTab('positions')}
      >
        Права
      </Button>
    </div>
  );

  if (tab === 'positions') {
    return (
      <>
        {tabs}
        <RightsTab />
      </>
    );
  }

  if (screen.isPending) return <Loading text="Загружаю пользователей…" />;
  if (screen.error || !screen.data) return <ErrorState onRetry={() => screen.refetch()} />;

  const { users, requests, services, positions, employees_without_account } = screen.data;
  const busy = setAccess.isPending || revokeAccess.isPending || setPosition.isPending;

  const ownerIds = new Set(positions.filter((item) => item.is_owner).map((item) => item.id));

  return (
    <>
      {tabs}
      <Card
        title="Пользователи"
        actions={
          <Button
            variant="primary"
            onClick={() => api.invite().then((answer) => setInvite(answer.how))}
          >
            + Пригласить
          </Button>
        }
      >
        {users.length === 0 ? (
          <Empty
            title="Пользователей пока нет"
            text="Первым в системе появляется владелец — по одноразовому коду из make create-host."
          />
        ) : (
          <>
            <div className="table-wrap matrix-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>Имя</th>
                    <th>Должность</th>
                    {services.map((service) => (
                      <th key={service} className="table__cell--service">
                        {serviceTitle(service)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id}>
                      <td>
                        <div className="table__name">
                          {displayName(user)}
                          {user.user_id === meUserId && <span className="faint"> (вы)</span>}
                        </div>
                        <Button small variant="quiet" onClick={() => setRightsOf(user)}>
                          Права
                        </Button>
                        {user.employee_status === 'fired' && (
                          <div className="table__sub">уволен</div>
                        )}
                      </td>
                      <td>
                        <PositionCell
                          user={user}
                          positions={positions}
                          busy={busy}
                          onChange={(positionId) =>
                            setPosition.mutate({ userId: user.user_id, positionId })
                          }
                        />
                      </td>
                      {services.map((service) => (
                        <td key={service} className="table__cell--service">
                          <AccessCell
                            user={user}
                            service={service}
                            busy={busy}
                            onEdit={() => setEditing({ user, service })}
                            onRevoke={() =>
                              revokeAccess.mutate({ userId: user.user_id, service })
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="matrix-cards">
              {users.map((user) => (
                <div className="user-card" key={user.user_id}>
                  <div className="table__name">
                    {displayName(user)}
                    {user.user_id === meUserId && <span className="faint"> (вы)</span>}
                  </div>
                  <Button small variant="quiet" onClick={() => setRightsOf(user)}>
                    Права
                  </Button>
                  <div className="user-card__position">
                    <PositionCell
                      user={user}
                      positions={positions}
                      busy={busy}
                      onChange={(positionId) =>
                        setPosition.mutate({ userId: user.user_id, positionId })
                      }
                    />
                  </div>
                  <div className="user-card__services">
                    {services.map((service) => (
                      <div className="user-card__service" key={service}>
                        <span className="muted">{serviceTitle(service)}</span>
                        <AccessCell
                          user={user}
                          service={service}
                          busy={busy}
                          onEdit={() => setEditing({ user, service })}
                          onRevoke={() => revokeAccess.mutate({ userId: user.user_id, service })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {employees_without_account > 0 && (
          <p className="faint" style={{ marginTop: 'var(--space-4)' }}>
            Ещё {employees_without_account} сотрудник(ов) заведены в справочнике, но ни разу не
            открывали бота — выдать им доступ пока некуда.
          </p>
        )}
      </Card>

      <Card title={`Заявки (${requests.length})`}>
        {requests.length === 0 ? (
          <Empty title="Новых заявок нет" />
        ) : (
          requests.map((request) => (
            <div className="request" key={request.id}>
              <div className="request__main">
                <div className="table__name">
                  {request.applicant_name ?? 'Без имени'} →{' '}
                  {requestedServices(request).map(serviceTitle).join(', ')},{' '}
                  {formatShortDateTime(request.requested_at)}
                </div>
                {request.applicant_note && (
                  <div className="request__note">{request.applicant_note}</div>
                )}
              </div>
              <Button small onClick={() => setDeciding(request)}>
                Открыть
              </Button>
            </div>
          ))
        )}
      </Card>

      {invite !== null && (
        <Modal
          title="Как добавить сотрудника"
          onClose={() => setInvite(null)}
          actions={
            <Button variant="primary" onClick={() => setInvite(null)}>
              Понятно
            </Button>
          }
        >
          <p>{invite}</p>
        </Modal>
      )}

      {editing && (
        <AccessDialog
          user={editing.user}
          service={editing.service}
          onClose={() => setEditing(null)}
          onSubmit={(level) => {
            setAccess.mutate({
              userId: editing.user.user_id,
              service: editing.service,
              level,
            });
            setEditing(null);
          }}
        />
      )}

      {rightsOf && (
        <UserRightsDialog
          user={rightsOf}
          isOwner={rightsOf.position_id !== null && ownerIds.has(rightsOf.position_id)}
          onClose={() => setRightsOf(null)}
        />
      )}

      {deciding && (
        <RequestDialog
          request={deciding}
          positions={positions}
          services={services}
          onClose={() => setDeciding(null)}
          onApprove={(body) => {
            approve.mutate({ requestId: deciding.id, body });
            setDeciding(null);
          }}
          onReject={() => {
            reject.mutate(deciding.id);
            setDeciding(null);
          }}
        />
      )}
    </>
  );
}

function displayName(user: UserRow): string {
  return user.full_name ?? 'Аккаунт без сотрудника';
}

function PositionCell({
  user,
  positions,
  busy,
  onChange,
}: {
  user: UserRow;
  positions: PositionOption[];
  busy: boolean;
  onChange: (positionId: number | null) => void;
}) {
  // Пустое значение — это «должность не назначена», а не «выберите».
  // Разница существенная: первое описывает состояние человека, второе
  // командует читателю и ничего не сообщает (§2 docs/interface-principles.md).
  //
  // Должность, которой нет в справочнике, всё равно показывается. Так
  // бывает, если её убрали уже после назначения. Молча подставить
  // «не назначена» было бы враньём: у человека должность есть, и
  // управляющий должен видеть, какая именно, прежде чем менять.
  const known = positions.some((position) => position.id === user.position_id);
  const orphan =
    !known && user.position_id !== null
      ? [{ id: user.position_id, name: `${user.position_name ?? 'должность'} (снята из справочника)` }]
      : [];

  return (
    <Select
      aria-label={`Должность: ${displayName(user)}`}
      value={user.position_id ?? ''}
      disabled={busy}
      onChange={(event) => {
        const chosen = event.target.value === '' ? null : Number(event.target.value);
        // «Собственник» открывает всё, включая финансы, и вход во все три
        // сервиса (§8.7). Такое не делается случайным выбором в списке.
        const owner = positions.find((position) => position.id === chosen)?.is_owner;
        if (
          owner &&
          !window.confirm(
            `${displayName(user)} получит все права во всех сервисах, включая финансы, ` +
              'и вход во все три сервиса. Назначить «Собственником»?',
          )
        ) {
          return;
        }
        onChange(chosen);
      }}
    >
      <option value="">должность не назначена</option>
      {[...orphan, ...positions].map((position) => (
        <option key={position.id} value={position.id}>
          {'is_owner' in position && position.is_owner
            ? `${position.name} — все права`
            : position.name}
        </option>
      ))}
    </Select>
  );
}


function AccessCell({
  user,
  service,
  busy,
  onEdit,
  onRevoke,
}: {
  user: UserRow;
  service: string;
  busy: boolean;
  onEdit: () => void;
  onRevoke: () => void;
}) {
  const level = user.access[service];

  // Владелец виден во всех трёх колонках как `host`, и отсюда это не
  // меняется: `is_host` — не уровень доступа к сервису (D28), а признак
  // владельца, и снимается он не на этом экране.
  if (user.is_host) {
    return <LevelBadge level="host" />;
  }

  if (!level) {
    return (
      <Button small variant="quiet" disabled={busy} onClick={onEdit} title="Выдать доступ">
        —
      </Button>
    );
  }

  return (
    <span className="row" style={{ justifyContent: 'center' }}>
      <Button small variant="quiet" disabled={busy} onClick={onEdit} title="Изменить уровень">
        <LevelBadge level={level} />
      </Button>
      <Button
        small
        variant="quiet"
        disabled={busy}
        onClick={onRevoke}
        aria-label={`Отозвать доступ к сервису ${serviceTitle(service)}`}
        title="Отозвать доступ"
      >
        ×
      </Button>
    </span>
  );
}

function AccessDialog({
  user,
  service,
  onClose,
  onSubmit,
}: {
  user: UserRow;
  service: string;
  onClose: () => void;
  onSubmit: (level: string) => void;
}) {
  const [level, setLevel] = useState(user.access[service] ?? 'user');
  return (
    <Modal
      title={`Доступ: ${displayName(user)} → ${serviceTitle(service)}`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" onClick={() => onSubmit(level)}>
            Сохранить
          </Button>
        </>
      }
    >
      <Field label="Уровень внутри сервиса">
        <Select value={level} onChange={(event) => setLevel(event.target.value)}>
          {LEVELS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      </Field>
      <p className="faint" style={{ marginTop: 'var(--space-3)' }}>
        Уровень admin назначает и снимает только владелец. Доступ выдаётся отдельно к каждому
        сервису: доступ к обучению не открывает финансы.
      </p>
    </Modal>
  );
}

/** Сервисы заявки. Заявка одна на все боты (D187); `service` — запас на
 * случай ответа старого сервера без списка. */
function requestedServices(request: PendingRequest): string[] {
  return request.services?.length ? request.services : [request.service];
}

function RequestDialog({
  request,
  positions,
  services,
  onClose,
  onApprove,
  onReject,
}: {
  request: PendingRequest;
  positions: PositionOption[];
  services: string[];
  onClose: () => void;
  onApprove: (body: Record<string, unknown>) => void;
  onReject: () => void;
}) {
  const [positionId, setPositionId] = useState<string>('');
  const [level, setLevel] = useState<string>('user');
  const [chosen, setChosen] = useState<string[]>(requestedServices(request));

  const toggleService = (service: string) => {
    setChosen((current) =>
      current.includes(service)
        ? current.filter((item) => item !== service)
        : [...current, service],
    );
  };

  return (
    <Modal
      title={`Заявка от ${request.applicant_name ?? 'без имени'}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="danger" onClick={onReject}>
            Отклонить
          </Button>
          <Button
            variant="primary"
            disabled={chosen.length === 0}
            onClick={() =>
              onApprove({
                services: chosen,
                access_level: level,
                position_id: positionId ? Number(positionId) : null,
              })
            }
          >
            Одобрить
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          Просит доступ: {requestedServices(request).map(serviceTitle).join(', ')}.{' '}
          Подана {formatShortDateTime(request.requested_at)}
        </p>
        {request.applicant_note && <p className="request__note">{request.applicant_note}</p>}

        <Field label="Должность">
          <Select value={positionId} onChange={(event) => setPositionId(event.target.value)}>
            <option value="">не назначать</option>
            {positions.map((position) => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Уровень внутри сервиса">
          <Select value={level} onChange={(event) => setLevel(event.target.value)}>
            {LEVELS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </Field>

        <div className="field">
          <span className="field__label">К каким сервисам открыть доступ</span>
          <div className="row">
            {services.map((service) => (
              <label key={service} className="row" style={{ gap: 'var(--space-1)' }}>
                <input
                  type="checkbox"
                  checked={chosen.includes(service)}
                  onChange={() => toggleService(service)}
                />
                {serviceTitle(service)}
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
